import { useCallback, useState } from 'react';

import { useLazyGetProductFileQuery } from '@app/api/Products';
import { useUserPrint } from '@app/hooks';
import type {
  ProductImageFile,
  ProductImagesUploaderProps,
} from '@app/pages/Warehouses/Products/components/AddProduct/components/ProductImagesUploader/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

export const useProductImagesUploader = ({
  fileList,
  setFileList,
}: ProductImagesUploaderProps) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const isSorting = draggedIndex !== null;

  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const { showNotification } = useNotification();
  const [getProductFile] = useLazyGetProductFileQuery();

  const handlePreview = useCallback(
    async (file: ProductImageFile) => {
      try {
        const response = await getProductFile({
          fileId: Number(file.uid),
          ActiveBranchId,
        }).unwrap();

        if (response?.Data) {
          const base64Url = `data:image/jpeg;base64,${response.Data}`;
          setPreviewImage(base64Url);
          setPreviewVisible(true);
        }
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [ActiveBranchId, getProductFile, showNotification]
  );

  const handleCancel = useCallback(() => setPreviewVisible(false), []);

  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
  }, []);

  const handleDrop = useCallback(
    (targetIndex: number) => {
      if (draggedIndex === null || draggedIndex === targetIndex) return;

      const updatedList = [...fileList];
      const [movedItem] = updatedList.splice(draggedIndex, 1);
      updatedList.splice(targetIndex, 0, movedItem);

      setFileList(updatedList);
      setDraggedIndex(null);
    },
    [draggedIndex, fileList, setFileList]
  );

  const preventUploadDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      if (!isSorting) return;

      e.preventDefault();
      e.stopPropagation();

      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'none';
      }
    },
    [isSorting]
  );

  return {
    handlePreview,
    handleDragStart,
    handleDrop,
    handleDragEnd,
    previewVisible,
    handleCancel,
    previewImage,
    isSorting,
    preventUploadDrop,
  };
};
